
import {
  computed,
  ComputedRef,
  defineComponent,
  onMounted,
  ref,
  Ref,
  watch
} from "vue";
import Cropper from "cropperjs";
import "cropperjs/src/css/cropper.css";
import { useStore } from "vuex";
import { wrapLoading } from "../../utils/wrap_loading";
import {
  disableBodyOverflow, dispatchErrorBox,
  enableBodyOverflow,
  handleImageExtention
} from "../mixins";

export default defineComponent({
  name: "cropperImageInput",
  props: {
    imageUrl: {
      type: String,
      required: true
    }
  },
  emits: ["cropped-image-url"],
  setup(props, { emit }) {
    const store = useStore();
    const isCuttable: Ref<boolean> = ref(false);
    const isInputImageOpened: ComputedRef<boolean> = computed(
      () => store.getters["utils_module/isVisible"]
    );
    const cropper = ref();
    const url: Ref<string> = ref(props.imageUrl);

    onMounted(() => {
      loadCropper();
    });

    watch(
      () => url.value,
      () => {
        loadCropper();
      }
    );
    const loadCropper = () => {
      let image = document.querySelector(
        "[data-user-image]"
      ) as HTMLImageElement;
      if (cropper.value) {
        cropper.value.destroy();
      }

      image.src = url.value;
      cropper.value = new Cropper(image, {
        aspectRatio: 1,
        viewMode: 1,
        checkCrossOrigin: false,
        ready: function() {
          isCuttable.value = true;
        }
      });
      disableBodyOverflow();
    };

    const getCroppedImageUrl = async (): Promise<void> => {
      const input = document.querySelector("[data-file]") as HTMLInputElement;

      if (!input || !input.files || input.files.length === 0) {
        console.error("Nenhuma imagem selecionada.");
        return;
      }

      const file = input.files[0];
      /**
       * Sets the maximum allowed file size to 3 MB.
       * - 3: Represents the amount of megabytes.
       * - 1024: Means the number of kilobytes per megabyte (1 MB = 1024 KB).
       * - 3 * 1024 * 1024: Multiply 3 megabytes by 1024 twice to get the value in bytes (3 MB = 3 * 1024 * 1024 bytes).
       */
      const maxSize = 3 * 1024 * 1024;

      if (file.size > maxSize) {
        await dispatchErrorBox("A imagem selecionada é maior que 3MB. Por favor, selecione outra.");
        return;
      }

      await wrapLoading(async () => {
        if (!isCuttable.value) {
          return;
        }

        emit("cropped-image-url", cropper.value.getCroppedCanvas().toDataURL());
        await closeCropper();
      });
    };

    const closeCropper = async (): Promise<void> => {
      await store.dispatch("utils_module/setVisibility", false);
      enableBodyOverflow();
    };

    const setImageToPreview = async (
      input: HTMLInputElement
    ): Promise<void> => {
      const filename = input?.files![0].name;

      if (handleImageExtention(filename) === "") return;

      const reader = new FileReader();
      reader.readAsDataURL(input.files![0]);
      reader.onload = () => {
        url.value = reader.result as string;
      };
    };

    document.addEventListener("keydown", async function(e) {
      if (e.key === "Escape") {
        await store.dispatch("utils_module/setVisibility", false);
      }
    });

    return {
      url,
      isInputImageOpened,
      loadCropper,
      getCroppedImageUrl,
      setImageToPreview,
      closeCropper
    };
  }
});
